import clsx from "clsx";
import { runtimeConfig } from "../../lib/config";
import { MailBarcode } from "../../components/MailBarcode";
import styles from "./LettermanBrand.module.css";

/**
 * The Letterman lockup: a perforated stamp beside a stencilled wordmark — the
 * two marks a mail room puts on everything it touches. The lib's <Brand /> is
 * a wordmark lockup with no mark, so this replaces it rather than configuring
 * it: the rail and the sign-in envelope both need the stamp on its own.
 *
 *   <LettermanBrand />      sidebar — stamp + wordmark + instance tagline
 *   <LettermanStamp />      the rail, where a wordmark would be three cut-off
 *                           letters (adminer 0.44's Sidebar.brandMark)
 *   <LettermanEnvelope />   sign-in — return address, franked stamp, cancellation
 */

export interface LettermanStampProps {
  /** Rendered size. `lg` is the sign-in envelope's franked stamp. */
  size?: "md" | "lg";
  className?: string;
}

/** The stamp: the perforations are holes the page punches out of the gold. */
export function LettermanStamp({ size = "md", className }: LettermanStampProps) {
  return (
    <span
      className={clsx(styles.stamp, size === "lg" && styles.stampLg, className)}
      aria-hidden="true"
    >
      <span className={styles.stampInner}>
        L{size === "lg" && <small>MAIL</small>}
      </span>
    </span>
  );
}

/** Ring and waves — a cancellation with no message behind it. */
function CancelMark() {
  return (
    <svg className={styles.cancel} viewBox="0 0 120 70" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="2.2">
        <circle cx="34" cy="34" r="26" strokeWidth="2" />
        <path d="M60 18 q 6.25 -4 12.5 0 t 12.5 0 t 12.5 0 t 12.5 0 t 12.5 0" />
        <path d="M60 28 q 6.25 -4 12.5 0 t 12.5 0 t 12.5 0 t 12.5 0 t 12.5 0" />
        <path d="M60 38 q 6.25 -4 12.5 0 t 12.5 0 t 12.5 0 t 12.5 0 t 12.5 0" />
        <path d="M60 48 q 6.25 -4 12.5 0 t 12.5 0 t 12.5 0 t 12.5 0 t 12.5 0" />
      </g>
    </svg>
  );
}

export function LettermanBrand({ className }: { className?: string }) {
  return (
    <div className={clsx(styles.brand, className)}>
      <LettermanStamp />
      <span className={styles.lockup}>
        <span className={styles.wordmark}>Letterman</span>
        <span className={styles.tagline}>
          Mail · <b>{runtimeConfig.version}</b>
        </span>
      </span>
    </div>
  );
}

/**
 * The sign-in envelope's head — rendered in `LoginForm`'s `brand` slot, which
 * is why the address block and the franking live together in one node.
 */
export function LettermanEnvelope() {
  return (
    <div className={styles.envelope}>
      <div className={styles.return}>
        <b>Letterman</b>
        <br />
        Mail · {runtimeConfig.version}
        <br />
        {window.location.host}
      </div>
      <div className={styles.franking}>
        <CancelMark />
        <LettermanStamp size="lg" />
      </div>
    </div>
  );
}

/** The barcode along the envelope's bottom edge — `LoginForm`'s `footer`. */
export function LettermanEnvelopeFooter() {
  return (
    <div className={styles.envelopeFooter}>
      <MailBarcode value={`letterman ${runtimeConfig.version}`} />
      <span className={styles.build}>{runtimeConfig.version}</span>
    </div>
  );
}
